import React from 'react';
import { Table, Tag, Typography, Card } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faPenToSquare,
  faTimesCircle,
  faDownload,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from 'src/components/design-system';
import { useUploadContextFactory } from './upload-contexts/use-upload-context-factory';
import styles from './upload-results.module.scss';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatNumber } from 'shared/utils';

const { Title, Text } = Typography;

const UploadResults = ({ contextType }) => {
  const lang = useSelector(getLang('SETTINGS'));
  const { results } = useUploadContextFactory({ contextType });
  const { new: newItems = [], updated = [], removed = [], failed = [] } = results;

  const allResults = [
    ...newItems.map(item => ({ ...item, status: 'new' })),
    ...updated.map(item => ({ ...item, status: 'updated' })),
    ...removed.map(item => ({ ...item, status: 'removed' })),
    ...failed.map(item => ({ ...item, status: 'failed' })),
  ];

  const columns = [
    {
      title: lang.FIRST_NAME,
      dataIndex: 'firstName',
      key: 'firstName',
      width: 150,
    },
    {
      title: lang.EMAIL,
      dataIndex: 'email',
      key: 'email',
      width: 250,
    },
    {
      title: lang.STATUS,
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: status => {
        const statusConfig = {
          new: {
            color: 'success',
            icon: <FontAwesomeIcon icon={faCheckCircle} />,
            text: lang.CREATED,
          },
          updated: {
            color: 'processing',
            icon: <FontAwesomeIcon icon={faPenToSquare} />,
            text: lang.UPDATED,
          },
          removed: {
            color: 'default',
            icon: <FontAwesomeIcon icon={faTrash} />,
            text: lang.REMOVED,
          },
          failed: {
            color: 'error',
            icon: <FontAwesomeIcon icon={faTimesCircle} />,
            text: lang.FAILED,
          },
        };
        const config = statusConfig[status];
        return (
          <Tag color={config.color} icon={config.icon} className={styles.statusTag}>
            {config.text}
          </Tag>
        );
      },
      filters: [
        { text: lang.CREATED, value: 'new' },
        { text: lang.UPDATED, value: 'updated' },
        { text: lang.REMOVED, value: 'removed' },
        { text: lang.FAILED, value: 'failed' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: lang.MESSAGE,
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (message, record) => {
        const successMessages = {
          new: lang.CONTACT_CREATED_SUCCESS,
          updated: lang.CONTACT_UPDATED_SUCCESS,
          removed: lang.CONTACT_REMOVED_SUCCESS,
        };
        return successMessages[record.status] ?? message;
      },
    },
  ];

  const downloadFailedRows = () => {
    if (failed.length === 0) return;

    const csvContent = [
      [lang.FIRST_NAME, lang.EMAIL, lang.ERROR_MESSAGE].join(','),
      ...failed.map(item =>
        [
          item.firstName || '',
          item.email || '',
          `"${(item.message || '').replace(/"/g, '""')}"`,
        ].join(','),
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'failed_rows.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={styles.container}>
      <Title level={3}>{lang.UPLOAD_RESULTS_TITLE}</Title>

      <div className={styles.summary}>
        <Card>
          <div className={styles.summaryStats}>
            <div className={styles.stat}>
              <FontAwesomeIcon icon={faCheckCircle} className={styles.successIcon} />
              <div>
                <Text strong className={styles.statCount}>
                  {formatNumber(newItems.length)}
                </Text>
                <br />
                <Text type="secondary">{lang.CREATED}</Text>
              </div>
            </div>
            <div className={styles.stat}>
              <FontAwesomeIcon icon={faPenToSquare} className={styles.updatedIcon} />
              <div>
                <Text strong className={styles.statCount}>
                  {formatNumber(updated.length)}
                </Text>
                <br />
                <Text type="secondary">{lang.UPDATED}</Text>
              </div>
            </div>
            {removed.length > 0 && (
              <div className={styles.stat}>
                <FontAwesomeIcon icon={faTrash} className={styles.removedIcon} />
                <div>
                  <Text strong className={styles.statCount}>
                    {formatNumber(removed.length)}
                  </Text>
                  <br />
                  <Text type="secondary">{lang.REMOVED}</Text>
                </div>
              </div>
            )}
            <div className={styles.stat}>
              <FontAwesomeIcon icon={faTimesCircle} className={styles.errorIcon} />
              <div>
                <Text strong className={styles.statCount}>
                  {formatNumber(failed.length)}
                </Text>
                <br />
                <Text type="secondary">{lang.FAILED}</Text>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {failed.length > 0 && (
        <div className={styles.downloadSection}>
          <Button
            type="primary"
            icon={<FontAwesomeIcon icon={faDownload} />}
            onClick={downloadFailedRows}
            danger>
            {lang.DOWNLOAD_FAILED_ROWS}
          </Button>
        </div>
      )}

      <Table
        columns={columns}
        dataSource={allResults}
        rowKey={record => record.id || `${record.email}`}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${formatNumber(range[0])}-${formatNumber(range[1])} of ${formatNumber(total)} items`,
        }}
        rowClassName={record => {
          return styles[`row-${record.status}`];
        }}
      />
    </div>
  );
};

export default UploadResults;
